// frontend/src/features/ogdb2/detail/tabs/FactsTab.jsx
import React, { useEffect, useState } from "react";

export default function FactsTab({ well, resolved, onPatchWell }) {
  const r = resolved || {};

  // Back-compat display: old field name was `assessor`, but we treat it as Appraisal Group.
  const appraisalGroupDisplay = well?.appraisal_firm || well?.assessor || "";

  // Local drafts so typing doesn’t spam PATCH
  const [draft, setDraft] = useState({
    well_name: "",
    operator: "",
    county: "",
    appraisal_firm: "",
    appraiser: "",
    consultant: "",
    working_interest_pct: "",
    royalty_interest_pct: "",
  });

  useEffect(() => {
    setDraft({
      well_name: well?.well_name || "",
      operator: well?.operator || "",
      county: well?.county || "",
      appraisal_firm: appraisalGroupDisplay || "",
      appraiser: well?.appraiser || "",
      consultant: well?.consultant || "",
      working_interest_pct:
        well?.working_interest_pct === null || well?.working_interest_pct === undefined
          ? ""
          : String(well.working_interest_pct),
      royalty_interest_pct:
        well?.royalty_interest_pct === null || well?.royalty_interest_pct === undefined
          ? ""
          : String(well.royalty_interest_pct),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [well?.id]);

  async function saveTextField(key) {
    const value = (draft[key] || "").trim();
    await onPatchWell?.({ [key]: value });
  }

  async function savePercentField(key) {
    const raw = String(draft[key] || "").trim();
    if (!raw) {
      // allow clearing
      await onPatchWell?.({ [key]: null });
      return;
    }
    const n = Number(raw.replace(/,/g, ""));
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      alert("Enter a percent from 0 to 100.");
      return;
    }
    await onPatchWell?.({ [key]: n });
  }

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-800">Facts</div>

      <div className="grid md:grid-cols-2 gap-3">
        <Info label="RRC #" value={well?.rrc_number} mono />
        <Info label="Tax Year" value={well?.tax_year} />

        <EditText
          label="Well Name"
          value={draft.well_name}
          onChange={(v) => setDraft((p) => ({ ...p, well_name: v }))}
          onBlur={() => saveTextField("well_name")}
        />
        <EditText
          label="Operator"
          value={draft.operator}
          onChange={(v) => setDraft((p) => ({ ...p, operator: v }))}
          onBlur={() => saveTextField("operator")}
        />

        <EditText
          label="County"
          value={draft.county}
          onChange={(v) => setDraft((p) => ({ ...p, county: v }))}
          onBlur={() => saveTextField("county")}
        />
        <EditText
          label="Appraisal Group"
          value={draft.appraisal_firm}
          onChange={(v) => setDraft((p) => ({ ...p, appraisal_firm: v }))}
          onBlur={() => saveTextField("appraisal_firm")}
          hint="(This replaces the old 'assessor' label)"
        />

        <EditText
          label="Appraiser"
          value={draft.appraiser}
          onChange={(v) => setDraft((p) => ({ ...p, appraiser: v }))}
          onBlur={() => saveTextField("appraiser")}
        />
        <EditText
          label="Consultant"
          value={draft.consultant}
          onChange={(v) => setDraft((p) => ({ ...p, consultant: v }))}
          onBlur={() => saveTextField("consultant")}
        />

        <EditPercent
          label="Working Interest %"
          value={draft.working_interest_pct}
          onChange={(v) => setDraft((p) => ({ ...p, working_interest_pct: v }))}
          onBlur={() => savePercentField("working_interest_pct")}
        />
        <EditPercent
          label="Royalty Interest %"
          value={draft.royalty_interest_pct}
          onChange={(v) => setDraft((p) => ({ ...p, royalty_interest_pct: v }))}
          onBlur={() => savePercentField("royalty_interest_pct")}
        />
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-800 mb-2">Quick Metrics (Resolved)</div>
        <div className="grid md:grid-cols-3 gap-2 text-sm">
		  <MetricRow label="Oil Price" v={r.oil_price} />
		  <MetricRow label="Production (BOE)" v={r.production_boe} />
		  <MetricRow label="LOE" v={r.loe} />

		  <MetricMoneyRow label="Taxable (Current Year)" v={r.taxable_current_year} />
		  <MetricMoneyRow label="Taxable (Prior Year)" v={r.taxable_prior_year} />
		</div>
        <div className="mt-3 text-xs text-gray-500">
          All negotiation inputs are editable in the <b>Valuation</b> tab (for Assessor / Actual / BA).
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-800 mb-1">Value History</div>
        <div className="text-xs text-gray-500">
          Coming next: a per-year history (Assessor value, Appraiser value, BA value, Settled value) so you can see the
          story of this property across years.
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={mono ? "font-mono text-gray-900" : "text-gray-900"}>{String(value ?? "—")}</div>
    </div>
  );
}

function EditText({ label, value, onChange, onBlur, hint }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">{label}</div>
        {hint ? <div className="text-[11px] text-gray-400">{hint}</div> : null}
      </div>
      <input
        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1 text-gray-900"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="—"
      />
    </div>
  );
}

function EditPercent({ label, value, onChange, onBlur }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <input
        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1 text-gray-900"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="e.g. 12.5"
      />
    </div>
  );
}

function MetricRow({ label, v }) {
  const unit = v?.unit ? ` ${v.unit}` : "";
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-gray-900">
        BA: <b>{fmt(v?.ba)}</b>
        <span className="text-gray-500">{unit}</span>
      </div>
      <div className="text-xs text-gray-500">
        Assessor {fmt(v?.assessor)} · Actual {fmt(v?.actual)}
      </div>
    </div>
  );
}

function MetricMoneyRow({ label, v }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-gray-900">
        BA: <b>{fmtMoney(v?.ba)}</b>
      </div>
      <div className="text-xs text-gray-500">
        Assessor {fmtMoney(v?.assessor)} · Actual {fmtMoney(v?.actual)}
      </div>
    </div>
  );
}

function fmt(x) {
  if (x === null || x === undefined || x === "") return "—";
  if (typeof x === "number") return x.toLocaleString();
  return String(x);
}

function fmtMoney(x) {
  if (x === null || x === undefined || x === "") return "—";
  if (typeof x === "number") return `$${x.toLocaleString()}`;
  const n = Number(String(x).replace(/,/g, ""));
  if (Number.isFinite(n)) return `$${n.toLocaleString()}`;
  return String(x);
}